import React, { useEffect, useRef, useState } from 'react'
import swap from 'lodash-move'
import { useSprings, config, animated } from '@react-spring/web'
import { useDrag } from 'react-use-gesture'
import { observer } from 'mobx-react-lite'

import { useStores } from '@trivie/core'
import { isAction, isCondition, isSectionHeader } from '@trivie/core/src/type-guards/isAction'
import { Step } from '../../content-creation/components/step-item'
import { InsertStepButton } from './insert-step-btn'

const clamp = require('lodash/clamp')

const fn = (open, order: number[], active = false, originalIndex = 0, curIndex = 0, y = 0) => {
  return (index: number) => {
    return active && index === originalIndex
      ? {
          y: curIndex * 134.52 + y,
          scale: 1.1,
          zIndex: 1,
          shadow: 18,
          immediate: (key: string) => key === 'y' || key === 'zIndex',
          config: (key: string) => (key === 'y' ? config.gentle : config.default),
        }
      : {
          y: order.indexOf(index) * 134.52,
          scale: 1,
          zIndex: 0,
          shadow: 0,
          immediate: !active && !open ? true : false,
        }
  }
}

export const DraggableList = observer(({ topic, workflow, readonly, handleAddStep }: any) => {
  const { toastStore } = useStores()
  const [items, setItems] = useState(workflow?.flat?.length ? workflow?.flat : [])
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    // If the steps list length changes (insert/removal),
    // then update the local items array and the order indicies ref
    if (workflow?.flat?.length) {
      setItems(workflow?.flat)
      order.current = workflow?.flat?.map((_, index) => index)
    }
  }, [
    workflow?.flat?.length,
    JSON.stringify(items),
    JSON.stringify(workflow?.flat),
    workflow?.stub,
  ])

  // Store indicies as a local ref, this represents the item order
  const order = useRef(items?.map((_, index) => index))

  // Create springs, each corresponds to an item, controlling its transform, scale, etc.
  const [springs, api] = useSprings(items?.length, fn(toastStore.open, order.current), [
    JSON.stringify(workflow?.flat),
    JSON.stringify(items),
    items?.length,
    toastStore?.open,
    isDragging,
  ])

  useEffect(() => {
    if (!isDragging) {
      // Finished dragging, no animations active, reorder the workflow json
      workflow?.reorder(order.current.map((newIdx) => items[newIdx]))
    }
  }, [isDragging])

  const bind = useDrag((state: any) => {
    const {
      event,
      args: [originalIndex],
      active,
      movement: [, y],
      dragging,
    } = state

    // Prevent dragging on svg or button elements or if readonly, inserting, or editing
    if (
      workflow?.stub ||
      topic ||
      readonly ||
      // editing ||
      event.target.tagName === 'path' ||
      event.target.tagName === 'svg' ||
      event.target.tagName === 'BUTTON' ||
      toastStore?.open
    ) {
      return
    }

    setIsDragging(dragging)

    const curIndex = order.current.indexOf(originalIndex)
    const curRow = clamp(Math.round((curIndex * 100 + y * 0.8) / 100), 0, items.length - 1)
    const newOrder = swap(order.current, curIndex, curRow)

    api.start(fn(toastStore.open, newOrder, active, originalIndex, curIndex, y))

    if (active) {
      // do something while dragging an item?
    } else {
      const firstItem = items[newOrder[0]]

      // Check if first item is an action (not allowed)
      if (isAction(firstItem)) {
        toastStore?.show({
          message: 'An action cannot be the first step in a path.',
          severity: 'error',
          icon: false,
          position: 'top',
          autoDismiss: 3000,
        })
        return // Don't update order
      }

      // Check if any actions are placed immediately after a section header
      let hasActionAfterSectionHeader = false
      for (let i = 1; i < newOrder.length; i++) {
        const item = items[newOrder[i]]
        const prevItem = items[newOrder[i - 1]]

        if (isAction(item) && isSectionHeader(prevItem)) {
          hasActionAfterSectionHeader = true
          break
        }
      }

      if (hasActionAfterSectionHeader) {
        toastStore?.show({
          message: 'Actions must be placed immediately after content, not section headers.',
          severity: 'error',
          icon: false,
          position: 'top',
          autoDismiss: 3000,
        })
        return // Don't update order, position will be restored
      }

      // Check if any actions are placed without ANY content before them in the path
      let hasInvalidActionPlacement = false
      for (let i = 0; i < newOrder.length; i++) {
        const item = items[newOrder[i]]

        if (isAction(item)) {
          // Look backwards from this position to the start of the path
          // Check if ANY content (condition) exists before this action
          let hasContentBefore = false
          for (let j = i - 1; j >= 0; j--) {
            const prevItem = items[newOrder[j]]
            // Check if any previous item is a content node (condition)
            if (isCondition(prevItem)) {
              hasContentBefore = true
              break // Found content, this action position is valid
            }
            // Section headers don't count as content, continue searching
          }

          if (!hasContentBefore) {
            hasInvalidActionPlacement = true
            break // Found an action with no content anywhere before it
          }
        }
      }

      if (hasInvalidActionPlacement) {
        toastStore?.show({
          message: 'Actions cannot be placed before content steps.',
          severity: 'error',
          icon: false,
          position: 'top',
          autoDismiss: 3000,
        })
        return // Don't update order, position will be restored
      }

      // Award certification can only be ordered after a complete_topic step
      let hasInvalidCertPlacement = false
      for (let i = 0; i < newOrder.length; i++) {
        const item = items[newOrder[i]]

        if (isAction(item) && item.action_type === 'award_certification') {
          let prevStep: any = null
          for (let j = i - 1; j >= 0; j--) {
            const prevItem = items[newOrder[j]]
            if (!isAction(prevItem)) {
              prevStep = prevItem
              break
            }
          }

          if (!isCondition(prevStep) || prevStep.condition_name !== 'complete_topic') {
            hasInvalidCertPlacement = true
            break
          }
        }
      }

      if (hasInvalidCertPlacement) {
        toastStore?.show({
          message: 'Certification actions must be placed after a "Complete Topic" step.',
          severity: 'error',
          icon: false,
          position: 'top',
          autoDismiss: 3000,
        })
        return // Don't update order, position will be restored
      }

      // All validations passed - update order
      if (
        isCondition(firstItem) ||
        (firstItem.object_type && firstItem.object_type === 'NodeGroup')
      ) {
        // Conditions and Section Headers can be first
        order.current = newOrder
        workflow?.recalculateGroups() // Recalculate after drag completes
        toastStore?.dismiss()
        toastStore?.clear()
      }
    }
  })

  // If readonly (detail view, return a normal list of steps with no actions available)
  if (topic || readonly) {
    return items.map((item, i) =>
      topic && i === 0 ? null : (
        <Step
          readonly={readonly}
          handleAddStep={() => {}}
          root={i === 0}
          step={item}
          node={
            isSectionHeader(item)
              ? item
              : isCondition(item)
                ? workflow?.nodes.find((n) =>
                    n.conditions.find((c) => c.criteria.includes(item.reference_id)),
                  )
                : workflow?.nodes.find((n) => n.actions.includes(item.reference_id))
          }
          index={i}
          style={{ marginBottom: 16 }}
        />
      ),
    )
  }

  return (
    <div style={{ height: items.length * 134.52 }}>
      {springs.map(({ zIndex, y, scale, shadow }, i) => {
        // Calculate the parent node for this item
        const node = isSectionHeader(items[i])
          ? items[i]
          : isCondition(items[i])
            ? workflow?.nodes.find((n: any) =>
                n.conditions.find((c: any) => c.criteria.includes(items[i].reference_id)),
              )
            : workflow?.nodes.find((n: any) => n.actions.includes(items[i].reference_id))

        // Check if this step's parent node is in a group (for indentation)
        const isInStepGroup =
          !isSectionHeader(items[i]) &&
          node &&
          workflow?.node_groups?.length > 0 &&
          workflow.node_groups.some((group: any) => group.node_ids.includes(node.node_id))

        return (
          <animated.div
            {...bind(i)}
            key={i}
            style={{
              boxShadow: shadow.to((s) => `rgba(0, 0, 0, 0.15) 0px ${s}px ${2 * s}px 0px`),
              borderRadius: 14,
              position: 'absolute',
              userSelect: 'none',
              width: isInStepGroup ? 'calc(100% - 64px)' : '100%',
              marginLeft: isInStepGroup ? 32 : 0,
              marginRight: isInStepGroup ? 32 : 0,
              cursor: workflow?.stub || readonly ? 'default' : isDragging ? 'grabbing' : 'grab',
              zIndex,
              y,
              scale,
            }}
            className="PathStep"
          >
            <Step
              readonly={readonly}
              handleAddStep={() => {}}
              root={i === 0}
              step={items[i]}
              node={
                isSectionHeader(items[i])
                  ? items[i]
                  : isCondition(items[i])
                    ? workflow?.nodes.find((n) =>
                        n.conditions.find((c) => c.criteria.includes(items[i].reference_id)),
                      )
                    : workflow?.nodes.find((n) => n.actions.includes(items[i].reference_id))
              }
              index={i}
            />
            {!isDragging && order.current.indexOf(i) !== items.length - 1 ? (
              <InsertStepButton
                workflow={workflow}
                readonly={readonly}
                step={items[i]}
                disabled={workflow?.stub || toastStore?.open}
                node={
                  isSectionHeader(items[i])
                    ? items[i]
                    : isCondition(items[i])
                      ? workflow?.nodes.find((n) =>
                          n.conditions.find((c) => c.criteria.includes(items[i].reference_id)),
                        )
                      : workflow?.nodes.find((n) => n.actions.includes(items[i].reference_id))
                }
                index={i}
                handleAddStep={handleAddStep}
              />
            ) : null}
          </animated.div>
        )
      })}
    </div>
  )
})
